import { useEffect } from 'react'
import type { ReactNode } from 'react'
import { RefreshCw, TriangleAlert } from 'lucide-react'
import type { SystemStatusDTO } from '@shared/types'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Skeleton
} from '@/components/ui'
import { formatNumber, pluralize } from '@/lib/format'
import { useSettingsStore } from '@/stores/useSettingsStore'

const DRIVER_LABELS: Record<SystemStatusDTO['ssh']['driver'], string> = {
  ssh2: 'ssh2 (in process)',
  system: 'system (ssh binary)'
}

const DRIVER_COPY: Record<SystemStatusDTO['ssh']['driver'], string> = {
  ssh2: 'Connects with the bundled ssh2 client. A private key is decrypted into memory for the length of one command and never touches the disk.',
  system: 'Runs the ssh binary on the host. The key is written to a 0600 file for the length of one command and deleted afterwards — useful where ssh2 cannot negotiate a connection.'
}

function Row({ label, children }: { label: string, children: ReactNode }): React.ReactElement {
  return (
    <div className="flex flex-col gap-1.5 border-b border-line py-3.5 first:pt-0 last:border-0 last:pb-0 sm:flex-row sm:gap-4">
      <span className="w-40 shrink-0 text-sm font-medium text-fg">{label}</span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  )
}

function Stat({ label, value }: { label: string, value: string }): React.ReactElement {
  return (
    <div className="rounded-control border border-line bg-sunken px-3 py-2.5">
      <p className="text-[11px] font-semibold tracking-[0.07em] text-fg-subtle uppercase">{label}</p>
      <p className="mt-0.5 text-lg leading-tight font-semibold tabular-nums text-fg">{value}</p>
    </div>
  )
}

export default function SystemStatusCard(): React.ReactElement {
  const status = useSettingsStore((state) => state.status)
  const statusLoading = useSettingsStore((state) => state.statusLoading)
  const loadStatus = useSettingsStore((state) => state.loadStatus)

  useEffect(() => {
    void loadStatus()
  }, [loadStatus])

  const claude = status?.claude ?? null
  const summary = claude
    ? `Claude Code CLI ${claude.available ? 'available' : 'unavailable'}${claude.version ? `, version ${claude.version}` : ''}.`
    : 'Checking host services.'

  const queue = status?.queue ?? null
  const slotsFree = queue !== null ? Math.max(0, queue.maxConcurrentUsers - queue.activeUsers) : 0

  return (
    <Card>
      <CardHeader>
        <CardTitle>System status</CardTitle>
        <CardDescription>
          The host-level services this instance depends on. They are shared by everyone who signs
          in, so what you see here is what everyone gets.
        </CardDescription>
      </CardHeader>

      <CardBody className="space-y-5">
        <p className="sr-only" role="status" aria-live="polite">
          {summary}
        </p>

        <div className="rounded-control border border-line bg-sunken p-3.5">
          <p className="text-sm font-semibold text-fg">One CLI, a fixed number of slots</p>
          <p className="mt-1 text-xs leading-relaxed text-fg-muted">
            The Claude Code CLI runs on the server host, not in your browser, and there is exactly
            one of it. Every account on this instance generates alt text through that same binary,
            the same authentication and the same rate limits. Because each in-flight image is a
            separate process on that host, only a fixed number of users may generate at once — a
            fifth user on a four-slot instance is not turned away, their job waits in line and
            starts the moment a slot frees.
          </p>
        </div>

        {!status ? (
          <div className="space-y-3">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
            <div className="grid gap-3 sm:grid-cols-3">
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
            </div>
          </div>
        ) : (
          <>
            <div>
              <Row label="Claude Code CLI">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={status.claude.available ? 'success' : 'danger'}>
                    {status.claude.available ? 'Available' : 'Unavailable'}
                  </Badge>
                  <span className="text-sm text-fg-muted">
                    {status.claude.version ?? 'Version not reported'}
                  </span>
                </div>
                <p className="mt-1.5 font-mono text-xs break-all text-fg-subtle">
                  {status.claude.path || 'No binary resolved on this host'}
                </p>
                {status.claude.error ? (
                  <p className="mt-1.5 text-xs leading-relaxed text-bad-600 dark:text-bad-300">
                    {status.claude.error}
                  </p>
                ) : null}
              </Row>

              <Row label="SSH driver">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="accent">{DRIVER_LABELS[status.ssh.driver]}</Badge>
                  {status.ssh.driver === 'system' ? (
                    <Badge variant={status.ssh.binaryAvailable ? 'success' : 'danger'}>
                      {status.ssh.binaryAvailable ? 'ssh binary found' : 'ssh binary missing'}
                    </Badge>
                  ) : null}
                </div>
                <p className="mt-1.5 text-xs leading-relaxed text-fg-muted">
                  {DRIVER_COPY[status.ssh.driver]}
                </p>
              </Row>

              <Row label="Generation slots">
                <div className="grid gap-3 sm:grid-cols-3">
                  <Stat
                    label="Slots in use"
                    value={`${formatNumber(status.queue.activeUsers)} / ${formatNumber(status.queue.maxConcurrentUsers)}`}
                  />
                  <Stat label="Users waiting" value={formatNumber(status.queue.waitingUsers)} />
                  <Stat
                    label="Images at once"
                    value={formatNumber(status.queue.globalConcurrency)}
                  />
                </div>
                <p className="mt-2 text-xs leading-relaxed text-fg-muted" aria-live="polite">
                  {status.queue.waitingUsers > 0
                    ? `Every slot on this instance is taken, so ${formatNumber(status.queue.waitingUsers)} ${pluralize(status.queue.waitingUsers, 'job')} is waiting in line — each one starts on its own as a slot frees.`
                    : `${formatNumber(slotsFree)} ${pluralize(slotsFree, 'slot')} free right now, so a generate you start begins immediately.`}
                  {` Those ${formatNumber(status.queue.globalConcurrency)} ${pluralize(status.queue.globalConcurrency, 'image')} are one shared pool, not an allowance each: whoever is generating splits it round robin, and a single user working alone gets all of it.`}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-fg-subtle">
                  {formatNumber(status.queue.queued)}{' '}
                  {pluralize(status.queue.queued, 'job')} queued and{' '}
                  {formatNumber(status.queue.running)}{' '}
                  {pluralize(status.queue.running, 'job')} running, counting every user on this
                  instance.
                </p>
              </Row>
            </div>

            {!status.claude.available ? (
              <div className="rounded-control border border-warn-500/40 bg-warn-100/70 p-3.5 dark:bg-warn-950/50">
                <p className="flex items-center gap-2 text-sm font-semibold text-warn-700 dark:text-warn-300">
                  <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />
                  No alt text can be generated until this is fixed
                </p>
                <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-xs leading-relaxed text-warn-700 dark:text-warn-300">
                  <li>
                    Install the CLI on the server host:{' '}
                    <code className="rounded bg-warn-500/15 px-1 py-0.5 font-mono">
                      npm install -g @anthropic-ai/claude-code
                    </code>
                  </li>
                  <li>
                    Run{' '}
                    <code className="rounded bg-warn-500/15 px-1 py-0.5 font-mono">claude</code> once
                    on that host and finish signing in. Argus can never prompt for this from the
                    browser — it reuses the host&rsquo;s authentication for every user.
                  </li>
                  <li>
                    If the binary is not on the server&rsquo;s PATH, set its absolute path under
                    Admin → Instance settings.
                  </li>
                  <li>Come back here and choose Recheck.</li>
                </ol>
              </div>
            ) : null}
          </>
        )}
      </CardBody>

      <CardFooter className="justify-between">
        <p className="text-xs text-fg-subtle">
          {status?.version ? `Argus v${status.version}` : 'Argus version not reported'}
        </p>
        <Button
          variant="secondary"
          size="sm"
          iconLeft={<RefreshCw />}
          loading={statusLoading}
          onClick={() => {
            void loadStatus()
          }}
        >
          Recheck
        </Button>
      </CardFooter>
    </Card>
  )
}

export { SystemStatusCard }
